import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { IReportLayoutSetting, IReportAction, IReportData, IReportMetadata, ISubColumns } from '../report-data.service';
import { IDashboardSort } from '@app/main/report/dashboard/dashboard-metadata.service';
import { GridApi, GridOptions } from 'ag-grid-community';
import { IBrowselistActionPermission, IBrowseListConfig } from '../../browselists/browselists-config.service';
import { ReportGridService } from '../report-grid.service';
import { BrowseListService } from '../../browselists/browselists.service';
import { ReportFiltersUtil } from '../report-filters-util.service';
import _ from 'underscore';
import { IExtendedWindow, IObject } from '@type_definitions/datatypes';
import { IReportActionModel } from '@app/main/report/report.component';
import { util } from '../../util';
import { ReportExportService } from '../export/report-export.service';
import { IStoredReport, StoredReportDataService } from '../stored-report-data.service';
import { ReportActionsService } from '../actions/report-actions.service';
import { TranslationService } from '../../translation/translation.service';
import { ReportLayoutSettingsService } from '@app/shared/report/report-layout-settings.service';
import { Subscription } from 'rxjs';
import { AuthService } from '@app/shared/services/auth/auth.service';

export interface IEmbeddedReportGridReady {
  sectionId:string;
  filters:IObject;
  gridApi:GridApi;
}

@Component({
  selector: 'embedded-report',
  template: `
    <h2 *ngIf="!gridInitialized">
      <activity-indicator [useDefaultText]="true"></activity-indicator>
    </h2>
    <div *ngIf="gridInitialized">
      <div class="report-info">
        <div class="report-comments" *ngIf="reportComments !== (reportComments | translate)" [innerHTML]="reportComments | translate | safe: 'html'"></div>
      </div>

      <div class="row actions-row">
        <div class="report-header-section report-header-section-actions xs-clearfix" *ngIf="showActions">
          <span>
            <div class="browse-list-actions form-horizontal" *ngIf="browselistConfig">
              <button class="btn btn-primary col-md-auto" (click)="addRow()" *ngIf="hasPermissionToAdd">{{newLabel}}</button>
              <button class="btn btn-danger col-md-auto" (click)="deleteRow()" *ngIf="hasPermissionToDelete" [busyButton]="deleting">
                <span translate><i class="fa fa-trash-o"></i> GA.BUTTONS.DELETE</span>
              </button>
            </div>
            <report-actions *ngIf="actionModel.actions?.length" [actions]="actionModel.actions" (onAction)="executeReportAction($event)" [disabled]="!gridInitialized" [busy]="actionExecuting"></report-actions>
          </span>
        </div>

        <div class="report-header-section report-header-section-layout" *ngIf="showOperations">
          <div class="report-operations">
            <button class="btn float-end" (click)="exportReport()" translate>GA.BUTTONS.EXPORT</button>
          </div>
        </div>
      </div>

      <div class="clearfix">
        <ga-ag-grid [gridId]="reportId" [fillHeight]="gridHeight" [gridOptions]="gridOptions" [gridInit]="true" [height]="height || 400" style="width:100%"></ga-ag-grid>
      </div>
    </div>
  `,
  styleUrls: [ './embedded-report.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class EmbeddedReportComponent implements OnInit, OnChanges, OnDestroy {
  private _queryFilters:IObject;
  private _reportDataLoading:boolean;

  // report id to be loaded
  @Input() public reportId:number;
  // stored report code
  @Input() public storedReport:string;
  // stored report data
  @Input() public storedReportData:IStoredReport;
  // grid options
  @Input() public gridOptions:GridOptions;
  // browselist config
  @Input() public browselistConfig:IBrowseListConfig;
  // browselist context for ga2/ga3 browselist
  @Input() public browselistContext:IObject;
  // report comments to be displayed as report info
  @Input() public reportComments:string;
  // section id, default value is stringify report id
  @Input() public sectionId:string;
  // layout setting code
  @Input() public layoutGridCode:string;
  // use layout setting
  @Input() public layoutSetting:IReportLayoutSetting;
  // report metadata
  @Input() public reportMetadata:IReportMetadata;
  @Input() public reportData:IReportData;
  @Input() public reportSort:IDashboardSort[];
  @Input() public subColumns:ISubColumns;
  // grid filling height configuration
  @Input() public gridHeight:string;
  // grid height configuration
  @Input() public height:number;
  // show report actions / browselist actions
  @Input() public showActions:boolean;
  // label for replacing new button when showActions is true
  @Input() public newLabel:string;
  // browselist title replace the one in browselistConfig.label (browselist config label should not be defined if title should be used)
  @Input() public title:string;
  // specify permission for browselist add/edit/delete
  @Input() public browselistActionPermission:IBrowselistActionPermission;
  // load row function
  @Input() public loadRowFn:() => Promise<string|IReportData>;
  // show operations (send mail / export) default: false
  @Input() public showOperations:boolean;
  // Template code to be used when exporting report
  @Input() public templateCode:string;
  // report data loading getter setter
  @Input()
  public get reportDataLoading():boolean {
    return this._reportDataLoading;
  }

  public set reportDataLoading(loading:boolean) {
    this._reportDataLoading = loading;
    this.reportDataLoadingChange.emit(loading);
  }
  // report query filters to be used whenever browselist add/edit/delete been executed, to reload rows
  @Input()
  public get queryFilters():IObject {
    return this._queryFilters;
  }

  public set queryFilters(queryFilters:IObject) {
    this._queryFilters = queryFilters;
    this.queryFiltersChange.emit(queryFilters);
  }

  // when metadata has been successfully installed / stored report has been loaded
  @Output() public onGridReady:EventEmitter<IEmbeddedReportGridReady> = new EventEmitter<IEmbeddedReportGridReady>();
  // when report action has been executed, will emit this event
  @Output() public onReportActionExecuted:EventEmitter<void> = new EventEmitter<void>();
  @Output() public queryFiltersChange:EventEmitter<IObject> = new EventEmitter<IObject>();
  @Output() public reportDataLoadingChange:EventEmitter<boolean> = new EventEmitter<boolean>();

  public deleting:boolean;
  public gridInitialized:boolean;
  public actionModel:IReportActionModel = {
    selectedAction: undefined,
    actions: []
  };
  public actionExecuting:boolean;

  private hasCustomActions:boolean;
  private overwriteExportCheckboxes:boolean;
  private storedReportLoading:boolean;
  private onLayoutSettingChangeSubscription:Subscription;
  private onLayoutSettingSavedSubscription:Subscription;
  /**
   * @description Stores the original report metadata fetched from the server or the initial layout
   * settings when the page was first loaded.
   */
  private originalReportMetadata:IReportMetadata;

  constructor(
    private reportGridService:ReportGridService,
    private browseListService:BrowseListService,
    private reportFiltersUtil:ReportFiltersUtil,
    private storedReportDataService:StoredReportDataService,
    private reportActionsService:ReportActionsService,
    private reportExportService:ReportExportService,
    private translationService:TranslationService,
    private reportLayoutSettingsService:ReportLayoutSettingsService,
    private authService:AuthService,
    @Inject(Window) window:IExtendedWindow
  ) {
    // define edit cell method to be usable from window object
    // referenced for popup renderer in ng1 report style service
    window.editCellFn = this.editCell;
  }

  public ngOnInit():void {
    // only set edit/delete row functions on the grid level, the trigger takes place with report-grid.service at on click event
    this.reportGridService.setEditRowFunction(this.sectionId, this.editRow);
    this.reportGridService.setDeleteRowFunction(this.sectionId, this.deleteRow);

    // register listener
    this.listen();
  }

  public ngOnChanges(changesObj:SimpleChanges):void {
    this.gridOptions = changesObj.gridOptions?.currentValue || this.gridOptions || {};

    if (changesObj.layoutSetting?.currentValue) {
      this.layoutSetting = changesObj.layoutSetting.currentValue;
    }

    if (changesObj.sectionId?.currentValue) {
      this.sectionId = changesObj.sectionId.currentValue;
    }

    if (changesObj.title?.currentValue) {
      this.title = changesObj.title.currentValue;
    }

    if (changesObj.newLabel?.currentValue) {
      this.newLabel = changesObj.newLabel.currentValue;
    }

    if (changesObj.browselistConfig?.currentValue) {
      this.browselistConfig = changesObj.browselistConfig.currentValue;
      this.reportId = this.browselistConfig.reportId;
      this.sectionId = this.sectionId || this.reportId.toString();
      this.title = this.browselistConfig.label || this.title;
      this.newLabel = this.newLabel || this.translationService.instant('GA.BUTTONS.NEW_LABEL', { label: this.title });
      // stored report bindings should more priority than in config
      this.storedReport = this.storedReport || this.browselistConfig.storedReport;

      this.reportGridService.start(this.browselistConfig.reportId.toString(), this.gridOptions, this.title, this.sectionId);
    }

    if (changesObj.reportId?.currentValue) {
      this.reportId = this.browselistConfig? `${this.browselistConfig.reportId}` : _.clone(changesObj.reportId.currentValue);
      this.sectionId = this.sectionId || this.reportId.toString();

      if (!this.browselistConfig) {
        this.reportGridService.start(this.reportId.toString(), this.gridOptions, this.title, this.sectionId);
      }
    }

    if (changesObj.reportComments?.currentValue) {
      this.reportComments = _.clone(changesObj.reportComments.currentValue);
    }

    if (changesObj.reportMetadata?.currentValue) {
      this.reportMetadata = _.clone(changesObj.reportMetadata.currentValue);

      if (changesObj.reportMetadata.isFirstChange()) {
        this.originalReportMetadata = util.deepClone(this.reportMetadata);
      }

      // set actions
      if (this.showActions) {
        this.actionModel.actions = this.reportMetadata.actions;
        this.hasCustomActions = !!this.reportMetadata.actions?.length;
      }

      // set permissions
      if (!this.browselistActionPermission) {
        this.browselistActionPermission = {
          add: this.reportMetadata.permissions?.insertAllowed,
          edit: this.reportMetadata.permissions?.updateAllowed,
          delete: this.reportMetadata.permissions?.deleteAllowed
        };
      }

      if (this.storedReport) {
        // fetch stored report only when there is no loading of stored report
        if (!this.storedReportLoading) {
          this.setLoading(true);

          this.getStoredReport()
            .finally(() => {
              this.setLoading(false);
            });
        }
      } else {
        this.installGridSection();
        // init grid when no stored report defined
        this.gridInitialized = true;
      }
    }

    if (changesObj.storedReport?.currentValue) {
      this.storedReport = changesObj.storedReport.currentValue;

      // fetch stored report only when there is no loading of stored report
      if (!this.storedReportLoading) {
        this.setLoading(true);

        this.getStoredReport()
          .finally(() => {
            this.setLoading(false);
          });
      }
    }

    if (changesObj.storedReportData?.currentValue) {
      this.storedReportData = _.clone(changesObj.storedReportData.currentValue);
      this.getStoredReport();
    }

    if (changesObj.reportDataLoading && !_.isUndefined(changesObj.reportDataLoading.currentValue)) {
      this.reportDataLoading = _.clone(changesObj.reportDataLoading.currentValue);
      this.reportGridService.setLoading(this.sectionId, this.reportDataLoading);
    }

    if (changesObj.reportData && !_.isUndefined(changesObj.reportData.currentValue)) {
      this.reportData = _.clone(changesObj.reportData.currentValue);
      this.reportGridService.setData(this.sectionId, this.reportData);
    }

    if (changesObj.subColumns && !_.isUndefined(changesObj.subColumns.currentValue)) {
      this.subColumns = _.clone(changesObj.subColumns.currentValue);
      this.reportGridService.installSubColumns(this.sectionId, this.subColumns, this.browselistActionPermission);
    }

    if (changesObj.reportSort && !_.isUndefined(changesObj.reportSort.currentValue)) {
      this.reportSort = _.clone(changesObj.reportSort.currentValue);
      this.reportGridService.clearSort(this.sectionId);
      _.forEach(this.reportSort, (aSort) => {
        this.reportGridService.addSort(this.sectionId, aSort.name, aSort.direction);
      });
    }

    if (changesObj.queryFilters && !_.isUndefined(changesObj.queryFilters.currentValue)) {
      this.queryFilters = _.clone(changesObj.queryFilters.currentValue);
    }
  }

  private listen():void {
    if (this.layoutGridCode) {
      // Subscription to handle layout setting changes dynamically for the current grid
      this.onLayoutSettingChangeSubscription = this.reportGridService.onLayoutSettingChange
        .subscribe((gridId:string):void => {
          if (gridId !== this.sectionId) {
            return;
          }

          this.layoutSetting = this.reportLayoutSettingsService.updateLayoutSettingsOnChange(<number>this.reportId, this.layoutGridCode, this.layoutSetting, this.reportMetadata, this.originalReportMetadata);
        });

      // Subscription to handle the "save" event for layout settings
      // We need to make "layoutSetting" object up-to-date from server (because it might contain 'id')
      this.onLayoutSettingSavedSubscription = this.reportLayoutSettingsService.onSaved.subscribe(() => {
        this.layoutSetting = this.reportLayoutSettingsService.findLayoutSetting({ gridCode: this.layoutGridCode, ownerId: this.authService.getCurrentUser()?.id });
      });
    }
  }

  public get hasPermissionToAdd():boolean {
    return !this.browselistConfig?.noAdd && this.browselistActionPermission.add;
  }

  public get hasPermissionToDelete():boolean {
    return !this.browselistConfig?.noDelete && this.browselistActionPermission.delete;
  }

  public deleteRow = (rowIndex?:number):void => {
    this.deleting = true;
    this.browseListService.deleteRow(this.sectionId, this.browselistConfig, this.getQueryFilters(), this.loadRowFn, rowIndex, this.browselistContext, this.title)
      .finally(() => {
        this.deleting = false;
      });
  };

  public editRow = (visibleIndex:number):void => {
    this.browseListService.editRow(this.sectionId, this.browselistConfig, visibleIndex, this.getQueryFilters(), this.browselistContext, undefined, this.title, this.loadRowFn);
  };

  public editCell = (visibleIndex:number, config:IBrowseListConfig):Promise<any> => {
    return this.browseListService.editRow(this.sectionId, config, visibleIndex, this.getQueryFilters(), this.browselistContext, undefined, this.title, this.loadRowFn);
  };

  public addRow():void {
    this.browseListService.addRow(this.sectionId, this.browselistConfig, this.getQueryFilters(), this.browselistContext, this.loadRowFn, this.title);
  }

  public exportReport():void {
    this.reportExportService.exportReport(this.reportId, this.reportMetadata, this.getQueryFilters(), this.title, this.overwriteExportCheckboxes, this.templateCode);
  }

  public executeReportAction(selectedAction:IReportAction):void {
    this.actionExecuting = true;
    this.reportActionsService.executeAction(this.reportId, selectedAction)
      .then((actionExecuted:boolean) => {
        if (actionExecuted) {
          this.onReportActionExecuted.emit();
          if (this.reportActionsService.shouldReloadRows(selectedAction)) {
            this.loadRowFn();
          }
        }
      })
      .finally(() => {
        this.actionExecuting = false;
      });
  }

  private installGridSection():void {
    /**
     * Installs a grid section in the report layout. It first updates the column metadata
     * if layout settings are available. Then, it proceeds to install the grid using the
     * relevant services and configuration parameters. Once the installation is resolved,
     * it invokes the onGridResolved method.
     */
    if (this.layoutSetting) {
      this.reportLayoutSettingsService.updateColumnMetadata(this.reportMetadata, this.layoutSetting);
      this.originalReportMetadata = util.deepClone(this.reportMetadata);
    }

    this.reportGridService.install(this.sectionId, this.reportMetadata, this.hasCustomActions, this.title, this.browselistConfig, this.browselistActionPermission)
      .then(this.onGridResolved);
  }

  private getQueryFilters():IObject {
    return this.reportFiltersUtil.getQueryFiltersFromModel(this.queryFilters, this.reportMetadata, {
      applyDefaultValues: false,
      applyNullValues: true
    });
  }

  private onGridResolved = (gridApi:GridApi):Promise<void> => {
    this.onGridReady.emit({
      sectionId: this.sectionId,
      filters: this.queryFilters,
      gridApi
    });

    return Promise.resolve();
  };

  private setLoading(loading:boolean):void {
    this.storedReportLoading = loading;
    this.reportDataLoading = loading;
  }

  private getStoredReport():Promise<void> {
    // in dashboard the stored report is already loaded and provided via storedReportData input binding
    const storedReportPromise:Promise<IStoredReport> = this.storedReportData ? Promise.resolve(this.storedReportData) : this.storedReportDataService.loadStoredReport(this.reportId, this.storedReport);
    return storedReportPromise
      .then((storedReport:IStoredReport) => {
        if (storedReport) {
          this.overwriteExportCheckboxes = true;
          // set report filters
          const storedReportFilters:any = this.reportFiltersUtil.convertStoredReportToFilters(this.reportMetadata, storedReport, { transformCommaSeparatedToArray: false });
          this.queryFilters = util.copyAndExtend(storedReportFilters, this.queryFilters);
          this.reportFiltersUtil.updateColumnMetadata(this.reportMetadata, storedReport);
          return this.installGridSection();
        } else {
          return this.installGridSection();
        }
      })
      .then(() => {
        this.gridInitialized = true;
      });
  }

  public ngOnDestroy():void {
    this.reportGridService.stop();
    this.onLayoutSettingChangeSubscription?.unsubscribe();
    this.onLayoutSettingSavedSubscription?.unsubscribe();
  }
}